/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Where the sessionless card-fields frame is, if it will load at all.
 *
 * The browser cannot build that address: `client_id` is the key housien is
 * registered under at Payien and the base URL is server configuration. The
 * backend builds it — and, before answering, asks Payien whether the frame
 * would actually render. That probe is the whole point: a cross-origin
 * iframe that answers 403 is invisible from here. There is no readable load
 * event and no reachable status, just a white box inside the form. So the
 * answer carries `available` rather than leaving the host to guess from a
 * silence it cannot hear.
 */

import { useEffect, useRef, useState } from 'react';
import i18next from 'i18next';
import { OpenAPIHousien } from '@services/requests/core/OpenAPI';
import { request as __request } from '@services/requests/core/request';

/** Why there is no frame. `null` when there is one. */
export type FrameRefusal =
  /** Payien has no allowed_origins for this client. Production's state today. */
  | 'origin_not_registered'
  /** This deployment has no Payien base URL or key. */
  | 'not_configured'
  /** Payien rejected the request itself — a bug on our side. */
  | 'request_refused'
  /** Payien could not be asked. The only reason worth asking again. */
  | 'upstream_unavailable';

/** The frame's address, or why there will not be one. */
export interface FieldsFrame {
  /**
   * True while the backend has not answered yet.
   *
   * `available: false` alone cannot say whether the frame is missing or
   * merely late, and the two want opposite things on screen: a screen still
   * waiting may hold itself back, a screen that knows there is no frame must
   * not. Starting from `pending` keeps the host from treating silence as a
   * refusal.
   */
  pending: boolean;
  available: boolean;
  fields_url: string;
  /**
   * The frame's origin, for the host's postMessage check.
   *
   * Taken from here rather than parsed out of `fields_url`, so one parser
   * decides what the origin is instead of two that can disagree.
   */
  origin: string;
  reason: FrameRefusal | null;
}

/** What the host assumes before the answer arrives: no frame. */
const ABSENT: FieldsFrame = {
  pending: false, available: false, fields_url: '', origin: '', reason: null,
};

/**
 * What a failed request counts as.
 *
 * The route always answers 200, so a rejection means the call never arrived
 * or never came back: a connection that dropped, a request still in flight
 * when the browser gave up. From here that is the same situation as a Payien
 * that could not be reached — something transient, worth one more ask — and
 * it is the situation a slow connection actually lands in. It used to settle
 * as a plain "no frame" with no reason at all, which made it the one case
 * that was never asked again.
 */
const REQUEST_FAILED: FieldsFrame = { ...ABSENT, reason: 'upstream_unavailable' };

/**
 * How long to wait before each further ask.
 *
 * Only for a transient answer: a missing origin registration and a
 * deployment with no Payien configured are configuration, and will not fix
 * themselves while the buyer waits. Three asks in all, spaced so the second
 * covers a blip and the third covers a connection that is merely slow,
 * without the two of them together outrunning the ceiling below.
 */
const RETRY_DELAYS_MS = [2000, 3500];

/**
 * The longest the screen may be held waiting for an answer, whatever the
 * probe is doing.
 *
 * The screen used to open on the first answer, whatever it said, and that is
 * what put a finished-looking checkout on screen two and a half seconds
 * before the card fields arrived in it. Holding until the asks are done is
 * the fix; holding *forever* when a request neither answers nor rejects is
 * how that fix would become a worse bug than the one it replaced. So the
 * hold has an end, and the screen opens with whatever is known by then —
 * a frame that arrives after it still mounts into the space held for it.
 */
const PROBE_CEILING_MS = 9000;

/**
 * Asks the backend where the frame is.
 *
 * @param lang - The language the fields should be rendered in.
 * @returns What the backend answered.
 */
export function fetchFieldsFrame(lang: string): Promise<FieldsFrame> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/subscriptions/checkout-fields',
    query: { lang, _t: Date.now() },
  }) as Promise<FieldsFrame>;
}

/**
 * Waits, so a Payien that was briefly unreachable can be asked again.
 *
 * @param ms - How long to wait.
 * @returns A promise that settles after the wait.
 */
function pause(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

/**
 * Whether an answer is worth putting again.
 *
 * Only an unreachable Payien is. A missing origin registration and a
 * deployment with no Payien configured are both configuration: they will not
 * fix themselves while the buyer waits, and asking twice is one more round
 * trip to arrive at the same answer.
 *
 * @param answer - What the backend said.
 * @returns True when a second attempt could say something different.
 */
function worthRetrying(answer: FieldsFrame): boolean {
  return !answer.available && answer.reason === 'upstream_unavailable';
}

/**
 * Asks once, turning a failed request into "no frame".
 *
 * The route always answers 200, so a rejection here means the call never
 * arrived. The screen works without the frame — the session frame still
 * comes later — so this is not an error anyone needs to see.
 *
 * @param lang - The language the fields should be rendered in.
 * @returns The answer, or the absent frame.
 */
async function askOnce(lang: string): Promise<FieldsFrame> {
  try {
    return await fetchFieldsFrame(lang);
  } catch {
    return REQUEST_FAILED;
  }
}

/**
 * Asks, hands each answer over, and asks again while an answer is transient.
 *
 * Every answer is delivered, including the last refusal: `pending` is what
 * holds the screen, so an answer that never arrives is a screen that never
 * opens. `pending` is true only while another ask is actually coming.
 *
 * It sits outside the hook because this is the whole retry policy: the
 * effect's job is to start it once and to stop listening, and reading one
 * next to the other made neither legible.
 *
 * @param lang - The language the fields should be rendered in.
 * @param deliver - Where each answer goes; the caller decides what to do
 *   with one that arrives after it stopped caring.
 */
async function probeFrame(
  lang: string, deliver: (frame: FieldsFrame) => void,
): Promise<void> {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    const answer = await askOnce(lang);
    const again = worthRetrying(answer) && attempt < RETRY_DELAYS_MS.length;
    deliver({ ...answer, pending: again });
    if (!again) return;
    await pause(RETRY_DELAYS_MS[attempt]);
  }
}

/**
 * The frame's address, asked for once when the screen opens.
 *
 * Asked at mount and not when the form completes: the frame is meant to be
 * on screen at the first paint, before an agency exists and before a session
 * could be opened. The session reaches it later, by `arm`.
 *
 * A refusal is not an error and is never shown: the screen keeps the inert
 * preview of the five fields it already draws, and the ordinary session
 * frame still arrives later.
 *
 * @returns The answer, starting from "no frame" until one arrives.
 */
export function useFieldsFrame(): FieldsFrame {
  const [frame, setFrame] = useState<FieldsFrame>({ ...ABSENT, pending: true });
  // StrictMode mounts twice in development, and two probes would be two
  // round trips to Payien for one screen.
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return undefined;
    asked.current = true;
    let dropped = false;
    const deliver = (next: FieldsFrame) => { if (!dropped) setFrame(next); };
    void probeFrame(i18next.language || 'pt-BR', deliver);
    // The end of the hold, for a request that neither answers nor rejects:
    // a fetch with nothing at the other end sits there, and the screen is
    // waiting on `pending`, not on the request.
    // It releases the hold and nothing else: an answer that already arrived
    // is the answer, and replacing it here would take a frame back off a
    // screen that is already showing it.
    const ceiling = window.setTimeout(() => {
      if (dropped) return;
      setFrame((current) => (current.pending ? { ...current, pending: false } : current));
    }, PROBE_CEILING_MS);
    return () => { dropped = true; window.clearTimeout(ceiling); };
  }, []);

  return frame;
}
